import { useState, useCallback } from 'react';
import { DEFAULT_PERMISSIONS_BY_ROLE } from '@/features/external-users/constants/permissions';

export interface ExternalUserFormState {
  primarySocietyId: string;
  additionalSocietyIds: string[];
  civility?: 'M.' | 'Mme';
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  position?: string;
  roleType: 'ADMIN' | 'ACCOUNTANT' | 'SALES_MANAGER' | 'SALES' | 'SAV_MANAGER';
  permissions: string[];
  sendWelcomeEmail?: boolean;
}

const DEFAULT_FORM_STATE: ExternalUserFormState = {
  primarySocietyId: '',
  additionalSocietyIds: [],
  civility: undefined,
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  position: '',
  roleType: 'SALES',
  permissions: DEFAULT_PERMISSIONS_BY_ROLE['SALES'] || [],
};

export const useExternalUserFormWizard = (
  initialSocietyId?: string,
  userSocietyId?: string
) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ExternalUserFormState>(() => ({
    ...DEFAULT_FORM_STATE,
    primarySocietyId: initialSocietyId || userSocietyId || '',
  }));
  const [touchedSteps, setTouchedSteps] = useState<Set<number>>(new Set([1]));

  const totalSteps = 2;

  const updateFormData = useCallback((updates: Partial<ExternalUserFormState>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const goToNextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setTouchedSteps(prev => new Set(prev).add(nextStep));
    }
  }, [currentStep, totalSteps]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= totalSteps && touchedSteps.has(step)) {
      setCurrentStep(step);
    }
  }, [totalSteps, touchedSteps]);

  const resetForm = useCallback(() => {
    setFormData(DEFAULT_FORM_STATE);
    setCurrentStep(1);
    setTouchedSteps(new Set([1]));
  }, []);

  return { currentStep, totalSteps, formData, touchedSteps, updateFormData, goToNextStep, goToPreviousStep, goToStep, resetForm };
};
